import { getBotReasons, getBotSignals } from "./botDetection";

/**
 * Canal de origen de los eventos de ESTE repo (la SPA legacy).
 *
 * POR QUÉ EXISTE: los dos frontends escriben al MISMO proyecto de Mixpanel, así
 * que sin esta propiedad no hay forma de saber qué tráfico sale de cada uno.
 *
 * Catálogo — mantener sincronizado con `propi-web-nextjs`:
 *   `web-next` → la Next (App Router), en `src/lib/analytics/enrichment.ts`
 *   `web-spa`  → este repo
 */
const CHANNEL_ID = "web-spa";

/**
 * Autocapture: SOLO pageview. Homologado con `propi-web-nextjs`.
 *
 * Click, input, scroll y submit quedan apagados a propósito. Medido 12-19 ago
 * sobre 24.386 eventos de esta SPA: el autocapture era el 13,3% del volumen y el
 * 96,7% de eso era redundante — esta SPA emite 162 eventos propios que ya cargan
 * el 86,7% del volumen (`Home Viewed`, `Marketplace Card Clicked`,
 * `Marketplace Scroll Depth`, …), con más contexto. Lo único sin reemplazo son
 * `$mp_dead_click` (555) y `$mp_rage_click` (249); se sacrificaron a propósito, y
 * el session replay sigue cubriendo fricción de UX con más contexto que un
 * contador de clicks.
 *
 * ⚠️ NO APAGAR `pageview`. Se probó el 19-ago-2026 y rompió la medición:
 * `$mp_web_page_view` es el ÚNICO evento de pageview que llega a Mixpanel. Todo
 * usuario que entra y no dispara un evento de vista propio deja de existir en el
 * reporte — y en esta SPA hay pantallas sin evento de vista dedicado.
 *
 * Ya no hace falta apagarlo para frenar bots: el gate de `initMixpanel` corta
 * ANTES del init, así que un bot nunca llega a disparar el pageview automático.
 */
const AUTOCAPTURE_CONFIG = {
  pageview: "full",
  click: false,
  input: false,
  scroll: false,
  submit: false,
};

export const initMixpanel = (): void => {
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;
  if (!token) {
    console.log("Mixpanel token not found, skipping initialization");
    return;
  }

  // Gate de bots: si el cliente matchea alguna regla, el SDK NO se inicializa —
  // no se carga el bundle del CDN, no se emiten eventos y no se graba session
  // replay. Va acá y no en un wrapper porque los wrappers no cubren todo: quedan
  // fuera los `window.mixpanel.track(...)` sueltos (Login, VerifyLoginByCode).
  // Reglas homologadas con la Next; evidencia en
  // `propi-web-nextjs/src/lib/analytics/BOT_FILTERING.md`.
  const botReasons = getBotReasons();
  if (botReasons.length > 0) {
    if (process.env.NODE_ENV !== "production") {
      console.info("[analytics] Mixpanel bloqueado — reglas:", botReasons.join(", "));
    }
    return;
  }

  (function (e: Document, c: any) {
    if (!c.__SV) {
      window.mixpanel = c;
      c._i = [];

      c.init = function (q: string, r: any, f?: string) {
        function t(d: any, a: string) {
          const g = a.split(".");
          if (g.length === 2) {
            if (d[g[0]] === undefined) d[g[0]] = [];
            d = d[g[0]];
            a = g[1];
          }

          d[a] = function (...args: any[]) {
            
            d.push([a].concat(args));
          };
        }

        let b = c;
        if (typeof f !== "undefined") b = c[f] = [];
        else f = "mixpanel";

        const methods =
          "disable track track_pageview track_links track_forms register register_once alias identify name_tag set_config reset people.set".split(
            " "
          );

        for (let i = 0; i < methods.length; i++) {
          t(b, methods[i]);
        }

        c._i.push([q, r, f]);
      };

      c.__SV = 1.2;

      const k = e.createElement("script");
      k.type = "text/javascript";
      k.async = true;
      k.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";

      const s = e.getElementsByTagName("script")[0];
      s.parentNode?.insertBefore(k, s);
    }
  })(document, (window as any).mixpanel || []);

  window.mixpanel.init(token as any, {
    autocapture: AUTOCAPTURE_CONFIG,
    record_sessions_percent: 100,
  });

  // Super property: Mixpanel la inyecta en TODOS los eventos siguientes, sin que
  // ningún caller la pase. Se hace acá y no en un wrapper porque los wrappers no
  // cubren todo: quedan fuera los `window.mixpanel.track(...)` sueltos (Login,
  // VerifyLoginByCode) y los eventos de `autocapture`, que el SDK emite solo.
  //
  // Se registra después del `init` — antes, el stub aún no tiene la cola lista.
  // Sobrevive a toda la sesión porque esta SPA nunca llama `mixpanel.reset()`;
  // si algún día se agrega un reset en el logout, hay que volver a registrarla.
  //
  // Junto al canal viajan las señales de fingerprint en MODO OBSERVACIÓN
  // (`bot_*`). No bloquean nada: sirven para validar reglas candidatas con
  // evidencia antes de promoverlas. Como los bots detectados nunca llegan acá
  // (el gate de arriba corta antes), estas señales sólo describen tráfico que ya
  // pasó las 5 reglas — que es justo lo que queremos medir.
  window.mixpanel.register({ channel_id: CHANNEL_ID, ...getBotSignals() });
};